// License Management System - NFC-based Premium Features
import authManager from './authManager.js';
import nfcConfigManager from './nfcConfigManager.js';
import CryptoJS from 'crypto-js';

const PREMIUM_FEATURES = [
  'document_storage',
  'travel_info',
  'luggage_management',
  'pdf_export',
  'prize_challenges',
  'voice_notes',
  'advanced_budget',
  'cloud_sync',
  'multiple_trips'  // NEW: Gestión de múltiples viajes
];

class LicenseManager {
  constructor() {
    this.hasCheckedLicense = false;
    this.isPremiumUser = false;
    this.licenseType = null;
  }

  // Reset in-memory premium state. MUST be called on logout: this is a singleton
  // that survives account switches within the same tab (no page reload), so
  // without this the next user inherits the previous user's premium status until
  // the next server check — which hasFeatureAccess() skips while hasCheckedLicense
  // is still true.
  reset() {
    this.hasCheckedLicense = false;
    this.isPremiumUser = false;
    this.licenseType = null;
  }

  // Validate if NFC is a valid t-shirt key
  async isValidTShirtNFC(nfcKey) {
    try {
      return await nfcConfigManager.isValidNFCKey(nfcKey);
    } catch (error) {
      console.error('Failed to validate NFC key:', error);
      return false;
    }
  }

  // Get NFC ownership data
  getNFCOwnership(nfcKey) {
    const stored = localStorage.getItem(`nfc_ownership_${nfcKey}`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  // Set NFC ownership (first-time use)
  setNFCOwnership(nfcKey, userEmail) {
    const ownershipData = {
      owner: userEmail,
      claimedAt: Date.now(),
      temporaryUses: [] // Track temporary uses by other users
    };

    localStorage.setItem(`nfc_ownership_${nfcKey}`, JSON.stringify(ownershipData));
    console.log('🔒 NFC ownership claimed:', nfcKey, '→', userEmail);
    return ownershipData;
  }

  // Check if user can use NFC temporarily
  async canUseTemporary(nfcKey, userEmail) {
    const ownership = this.getNFCOwnership(nfcKey);
    if (!ownership) return false; // NFC not claimed yet
    if (ownership.owner === userEmail) return false; // Owner doesn't need temp access

    const now = Date.now();
    const cooldownDays = await nfcConfigManager.getCooldownDays();
    const cooldownMs = cooldownDays * 24 * 60 * 60 * 1000;

    // Check if user has used this NFC recently
    const userLastUse = ownership.temporaryUses.find(use => use.email === userEmail);
    if (userLastUse) {
      const timeSinceLastUse = now - userLastUse.usedAt;
      if (timeSinceLastUse < cooldownMs) {
        const remainingTime = Math.ceil((cooldownMs - timeSinceLastUse) / (24 * 60 * 60 * 1000));
        return {
          canUse: false,
          reason: `Must wait ${remainingTime} more days before using this NFC again`
        };
      }
    }

    return { canUse: true };
  }

  // Grant temporary premium access
  async grantTemporaryAccess(nfcKey, userEmail) {
    const ownership = this.getNFCOwnership(nfcKey);
    if (!ownership) throw new Error('NFC not claimed by any owner');

    const now = Date.now();
    const temporalDays = await nfcConfigManager.getTemporalDays();
    const tempExpiresAt = now + (temporalDays * 24 * 60 * 60 * 1000);

    // Update temporary uses
    const existingUseIndex = ownership.temporaryUses.findIndex(use => use.email === userEmail);
    if (existingUseIndex >= 0) {
      ownership.temporaryUses[existingUseIndex] = { email: userEmail, usedAt: now };
    } else {
      ownership.temporaryUses.push({ email: userEmail, usedAt: now });
    }

    // Save updated ownership
    localStorage.setItem(`nfc_ownership_${nfcKey}`, JSON.stringify(ownership));

    // Create temporary license
    const tempLicense = {
      type: 'nfc_temporary',
      nfcId: nfcKey,
      userEmail: userEmail,
      granted: now,
      expires: tempExpiresAt,
      valid: true,
      features: PREMIUM_FEATURES,
      isTemporary: true
    };

    localStorage.setItem('premium_license', JSON.stringify(tempLicense));

    this.isPremiumUser = true;
    this.licenseType = 'nfc_temporary';
    this.hasCheckedLicense = true;

    console.log(`🎁 Temporary premium granted for ${temporalDays} days:`, userEmail);

    // Dispatch event for UI updates
    window.dispatchEvent(new CustomEvent('license-updated', {
      detail: { isPremium: true, type: 'nfc_temporary', expiresAt: tempExpiresAt }
    }));

    return { success: true, type: 'nfc_temporary', expiresAt: tempExpiresAt };
  }

  // Check if user has premium license
  async checkPremiumAccess() {
    try {
      const user = authManager.getCurrentUser();

      if (!user) {
        this.isPremiumUser = false;
        this.licenseType = null;
        return false;
      }

      // FIRST: Check with server for latest premium status
      try {
        console.log('🔍 Checking server for premium status...');

        const serverResponse = await fetch('./api/check-premium.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userEmail: user.email,
            token: authManager.getToken()
          })
        });

        const serverResult = await serverResponse.json();

        if (serverResponse.ok && serverResult.success) {
          if (serverResult.isPremium && serverResult.license) {
            // Sync server license to localStorage
            localStorage.setItem('premium_license', JSON.stringify(serverResult.license));

            this.isPremiumUser = true;
            this.licenseType = serverResult.license.type;
            this.hasCheckedLicense = true;

            console.log('✅ Server sync: Premium status confirmed');
            console.log('🎫 License type:', serverResult.license.type);

            return true;
          } else {
            console.log('📋 Server sync: User has free tier');
            // Continue to check local storage as fallback
          }
        } else {
          console.warn('⚠️ Server premium check failed, using local fallback');
          // Continue to local check
        }
      } catch (error) {
        console.warn('⚠️ Server premium check error, using local fallback:', error.message);
        // Continue to local check
      }

      // Check if user authenticated with NFC (license key)
      if (user.authMethod === 'nfc' && user.nfcId) {
        this.isPremiumUser = true;
        this.licenseType = 'nfc_premium';
        console.log('🎫 Premium license detected via NFC:', user.nfcId);
        return true;
      }

      // Check if user has premium license stored
      const storedLicense = localStorage.getItem('premium_license');
      if (storedLicense) {
        try {
          const licenseData = JSON.parse(storedLicense);

          // Verify license belongs to current user
          if (licenseData.userEmail && licenseData.userEmail !== user.email) {
            console.warn('🚫 License belongs to different user:', licenseData.userEmail);
            localStorage.removeItem('premium_license');
            this.isPremiumUser = false;
            this.licenseType = 'free';
            return false;
          }

          // Verify license validity (in production, verify with server)
          if (licenseData.valid && licenseData.expires > Date.now()) {
            // Double-check NFC key validation if present
            if (licenseData.nfcId && !this.validateNFCKey(licenseData.nfcId, user.email)) {
              console.warn('🚫 Invalid NFC key for current user');
              localStorage.removeItem('premium_license');
              this.isPremiumUser = false;
              this.licenseType = 'free';
              return false;
            }

            this.isPremiumUser = true;
            this.licenseType = licenseData.type;
            console.log('🎫 Premium license verified:', licenseData.type);
            return true;
          }
        } catch (error) {
          console.warn('Invalid license data');
          localStorage.removeItem('premium_license');
        }
      }

      // Default to free tier
      this.isPremiumUser = false;
      this.licenseType = 'free';
      console.log('👤 Free tier user');
      return false;

    } catch (error) {
      console.error('License check failed:', error);
      this.isPremiumUser = false;
      this.licenseType = null;
      return false;
    }
  }

  // Check if specific feature is available
  async hasFeatureAccess(feature) {
    if (import.meta.env.DEV) {
      // DEV-only (localhost testing): the premium check hits PHP endpoints
      // (check-premium.php) absent from the Vite dev server. Instead, derive the
      // tier from a localStorage switch so BOTH free and premium can be tested
      // locally — toggle it with the DEV badge in the corner. Defaults to
      // premium. import.meta.env.DEV is false in production, so Vite strips this.
      this.isPremiumUser = localStorage.getItem('dev_tier') !== 'free';
      this.hasCheckedLicense = true;
    } else if (!this.hasCheckedLicense) {
      await this.checkPremiumAccess();
      this.hasCheckedLicense = true;
    }

    // Free tier features (always available)
    const freeFeatures = [
      'basic_notes',
      'city_exploration',
      'basic_plans',
      'useful_info',
      'groceries',
      'gifts',
      'lodging',
      'basic_summary'
    ];

    if (freeFeatures.includes(feature)) {
      return true;
    }

    // Premium features require license
    if (PREMIUM_FEATURES.includes(feature)) {
      return this.isPremiumUser;
    }

    // Unknown feature - default to free
    return true;
  }

  // Grant premium access (when NFC is scanned) - HYBRID SYSTEM
  async grantPremiumAccess(nfcKey, licenseType = 'nfc_premium') {
    try {
      // Get current user
      const user = authManager.getCurrentUser();
      if (!user || !user.email) {
        throw new Error('User must be authenticated to use NFC');
      }

      // Validate NFC is a valid t-shirt key
      if (!(await this.isValidTShirtNFC(nfcKey))) {
        throw new Error('Invalid NFC key - not a valid LOS DIAS merchandise');
      }

      const ownership = this.getNFCOwnership(nfcKey);

      // CASE 1: First time use - claim ownership permanently
      if (!ownership) {
        // Sync with server FIRST
        try {
          const serverResponse = await fetch('./api/activate-nfc.php', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              nfcId: nfcKey,
              userEmail: user.email,
              activationType: 'permanent',
              token: authManager.getToken()
            })
          });

          const serverResult = await serverResponse.json();

          if (!serverResponse.ok || !serverResult.success) {
            throw new Error(serverResult.error || 'Server activation failed');
          }

          // Server success - update local storage
          this.setNFCOwnership(nfcKey, user.email);
          localStorage.setItem('premium_license', JSON.stringify(serverResult.license));

          this.isPremiumUser = true;
          this.licenseType = 'nfc_permanent';
          this.hasCheckedLicense = true;

          console.log('🏆 Permanent premium activated! NFC claimed by:', user.email);
          console.log('✅ Server sync successful');

          window.dispatchEvent(new CustomEvent('license-updated', {
            detail: { isPremium: true, type: 'nfc_permanent' }
          }));

          return { success: true, type: 'nfc_permanent', message: '🏆 Permanent Premium Activated!' };

        } catch (error) {
          console.error('❌ Server sync failed:', error);
          throw new Error(`Failed to activate premium: ${error.message}`);
        }
      }

      // CASE 2: Owner using their own NFC
      if (ownership.owner === user.email) {
        // Reactivate permanent premium (in case it expired)
        const permanentLicense = {
          type: 'nfc_permanent',
          nfcId: nfcKey,
          userEmail: user.email,
          granted: Date.now(),
          expires: Date.now() + (365 * 24 * 60 * 60 * 1000),
          valid: true,
          features: PREMIUM_FEATURES,
          isPermanent: true
        };

        localStorage.setItem('premium_license', JSON.stringify(permanentLicense));

        this.isPremiumUser = true;
        this.licenseType = 'nfc_permanent';
        this.hasCheckedLicense = true;

        console.log('🔄 Premium reactivated for owner:', user.email);

        window.dispatchEvent(new CustomEvent('license-updated', {
          detail: { isPremium: true, type: 'nfc_permanent' }
        }));

        return { success: true, type: 'nfc_permanent', message: '✅ Welcome back, owner!' };
      }

      // CASE 3: Someone else trying to use it temporarily
      const canUseTemp = await this.canUseTemporary(nfcKey, user.email);
      if (!canUseTemp.canUse) {
        throw new Error(canUseTemp.reason);
      }

      // Sync temporary access with server FIRST
      try {
        const serverResponse = await fetch('./api/activate-nfc.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            nfcId: nfcKey,
            userEmail: user.email,
            activationType: 'temporary',
            token: authManager.getToken()
          })
        });

        const serverResult = await serverResponse.json();

        if (!serverResponse.ok || !serverResult.success) {
          throw new Error(serverResult.error || 'Server activation failed');
        }

        // Server success - update local storage
        await this.grantTemporaryAccess(nfcKey, user.email);
        localStorage.setItem('premium_license', JSON.stringify(serverResult.license));

        this.isPremiumUser = true;
        this.licenseType = 'nfc_temporary';
        this.hasCheckedLicense = true;

        console.log('🎁 Temporary premium activated for 7 days:', user.email);
        console.log('✅ Server sync successful');

        window.dispatchEvent(new CustomEvent('license-updated', {
          detail: { isPremium: true, type: 'nfc_temporary', expiresAt: serverResult.license.expires }
        }));

        return {
          success: true,
          type: 'nfc_temporary',
          message: serverResult.message,
          expiresAt: serverResult.license.expires
        };

      } catch (error) {
        console.error('❌ Temporary activation server sync failed:', error);
        throw new Error(`Failed to activate temporary premium: ${error.message}`);
      }

    } catch (error) {
      console.error('Failed to grant premium access:', error);
      return { success: false, error: error.message };
    }
  }

  // Grant premium access from friend's sharing
  async grantSharedPremiumAccess(sharedByEmail) {
    try {
      console.log('🤝 Granting shared premium access from:', sharedByEmail);

      const user = JSON.parse(localStorage.getItem('user_profile') || '{}');
      if (!user.email) {
        throw new Error('User not logged in');
      }

      // Grant 7-day temporary premium
      const sharedExpiresAt = new Date();
      sharedExpiresAt.setDate(sharedExpiresAt.getDate() + 7);

      const sharedLicense = {
        type: 'friend_shared',
        expires: sharedExpiresAt.toISOString(),
        granted: new Date().toISOString(),
        sharedBy: sharedByEmail,
        user: user.email
      };

      localStorage.setItem('premium_license', JSON.stringify(sharedLicense));
      this.isPremiumUser = true;
      this.licenseType = 'friend_shared';
      this.hasCheckedLicense = true;

      console.log('🎁 Friend-shared premium activated for 7 days');

      window.dispatchEvent(new CustomEvent('license-updated', {
        detail: { isPremium: true, type: 'friend_shared', expiresAt: sharedExpiresAt, sharedBy: sharedByEmail }
      }));

      return {
        success: true,
        type: 'friend_shared',
        message: `Premium access granted by ${sharedByEmail} for 7 days!`,
        expiresAt: sharedExpiresAt,
        sharedBy: sharedByEmail
      };

    } catch (error) {
      console.error('Failed to grant shared premium access:', error);
      return { success: false, error: error.message };
    }
  }

  // Revoke premium access
  revokePremiumAccess() {
    localStorage.removeItem('premium_license');
    this.isPremiumUser = false;
    this.licenseType = 'free';

    console.log('🚫 Premium access revoked');

    // Dispatch event for UI updates
    window.dispatchEvent(new CustomEvent('license-updated', {
      detail: { isPremium: false, type: 'free' }
    }));
  }

  // ADMIN: Completely reset user's premium status
  async adminRevokePremium(userEmail = null) {
    try {
      const targetEmail = userEmail || authManager.getCurrentUser()?.email;
      if (!targetEmail) {
        throw new Error('No user email provided');
      }

      console.log('🔧 [ADMIN] Revoking premium for:', targetEmail);

      // Clear local storage
      localStorage.removeItem('premium_license');
      this.isPremiumUser = false;
      this.licenseType = 'free';
      this.hasCheckedLicense = false;

      // Try to revoke on server too
      try {
        const response = await fetch('./api/admin-revoke-premium.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail: targetEmail })
        });

        if (response.ok) {
          console.log('✅ [ADMIN] Server premium revoked');
        }
      } catch (error) {
        console.warn('⚠️ [ADMIN] Server revoke failed, local only:', error.message);
      }

      // Dispatch event for UI updates
      window.dispatchEvent(new CustomEvent('license-updated', {
        detail: { isPremium: false, type: 'free' }
      }));

      console.log('✅ [ADMIN] Premium completely revoked for:', targetEmail);
      return { success: true, message: 'Premium access revoked' };

    } catch (error) {
      console.error('❌ [ADMIN] Failed to revoke premium:', error);
      return { success: false, error: error.message };
    }
  }

  // Get current license info
  getLicenseInfo() {
    return {
      isPremium: this.isPremiumUser,
      type: this.licenseType,
      features: this.isPremiumUser ? PREMIUM_FEATURES : [],
      hasChecked: this.hasCheckedLicense
    };
  }

  // Get blocked feature message
  getUpgradeMessage(feature) {
    const messages = {
      document_storage: {
        title: '🔒 Document Storage Premium',
        description: 'Safely store your travel documents, boarding passes, and ID photos.',
        benefit: 'Keep all important documents organized and accessible offline.'
      },
      luggage_management: {
        title: '🔒 Smart Luggage Premium',
        description: 'Advanced packing lists with smart suggestions and templates.',
        benefit: 'Never forget essentials with AI-powered packing recommendations.'
      },
      pdf_export: {
        title: '🔒 Trip Reports Premium',
        description: 'Export beautiful PDF reports of your complete travel experience.',
        benefit: 'Share professional trip summaries with friends and family.'
      },
      prize_challenges: {
        title: '🔒 Prize Challenges Premium',
        description: 'Participate in exclusive challenges and win amazing travel prizes.',
        benefit: 'Earn rewards, badges, and real prizes for your adventures.'
      },
      advanced_budget: {
        title: '🔒 Budget Analytics Premium',
        description: 'Detailed spending analytics with charts and forecasting.',
        benefit: 'Track every penny with advanced financial insights.'
      },
      cloud_sync: {
        title: '🔒 Cloud Sync Premium',
        description: 'Automatic backup and sync across all your devices.',
        benefit: 'Never lose your travel memories with secure cloud storage.'
      },
      multiple_trips: {
        title: '🔒 Multiple Trips Premium',
        description: 'Plan and keep more than one trip at the same time.',
        benefit: 'Free includes 1 active trip — go Premium for unlimited journeys.'
      }
    };

    return messages[feature] || {
      title: '🔒 Premium Feature',
      description: 'This feature requires a premium license.',
      benefit: 'Unlock the full travel experience.'
    };
  }

  // Get premium features list
  getPremiumFeatures() {
    return [
      {
        id: 'document_storage',
        name: 'Document Storage',
        icon: '📄',
        description: 'Secure document storage and management'
      },
      {
        id: 'luggage_management',
        name: 'Smart Luggage',
        icon: '🧳',
        description: 'Advanced packing lists and suggestions'
      },
      {
        id: 'pdf_export',
        name: 'Trip Reports',
        icon: '📊',
        description: 'Beautiful PDF trip summaries'
      },
      {
        id: 'prize_challenges',
        name: 'Prize Challenges',
        icon: '🏆',
        description: 'Win real prizes for completing challenges'
      },
      {
        id: 'advanced_budget',
        name: 'Budget Analytics',
        icon: '💰',
        description: 'Detailed spending insights and forecasting'
      },
      {
        id: 'cloud_sync',
        name: 'Cloud Sync',
        icon: '☁️',
        description: 'Automatic backup across devices'
      }
    ];
  }

  // Check if user can upgrade to premium
  canUpgradeToPremium() {
    const user = authManager.getCurrentUser();

    // User must be authenticated to upgrade
    if (!user) return false;

    // User must not already be premium
    if (this.isPremiumUser) return false;

    return true;
  }

  // FOR TESTING ONLY - Simulate friend sharing premium
  async simulateFriendSharedPremium(friendEmail = 'premium.friend@example.com') {
    console.log('🧪 [TEST] Simulating friend-shared premium activation...');

    try {
      const result = await this.grantSharedPremiumAccess(friendEmail);

      if (result.success) {
        // Show the modal
        window.dispatchEvent(new CustomEvent('nfc-activation-success', {
          detail: {
            type: 'friend_shared',
            message: result.message,
            sharedBy: friendEmail,
            expiresAt: result.expiresAt
          }
        }));
      }

      return result;
    } catch (error) {
      console.error('❌ [TEST] Friend-shared simulation failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Simulate premium upgrade flow (NFC t-shirt scan)
  async simulateNFCUpgrade() {
    try {
      // Get current user
      const user = authManager.getCurrentUser();
      if (!user || !user.email) {
        throw new Error('User must be authenticated to upgrade to premium');
      }

      // Simulate scanning a t-shirt NFC (use first valid key for simulation)
      const validKeys = await nfcConfigManager.getValidNFCKeys();
      const simulatedNfcKey = validKeys[0]; // 'DIAS_PREMIUM_TSHIRT_2025'

      console.log('🔍 Simulating NFC t-shirt scan...');
      console.log('👤 User:', user.email);
      console.log('👕 T-shirt NFC:', simulatedNfcKey);

      // Simulate NFC scan delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      const result = await this.grantPremiumAccess(simulatedNfcKey, 'nfc_premium');

      if (result.success) {
        return {
          success: true,
          message: result.message,
          nfcId: simulatedNfcKey,
          type: result.type,
          expiresAt: result.expiresAt
        };
      } else {
        return {
          success: false,
          message: result.error
        };
      }

    } catch (error) {
      console.error('Premium upgrade simulation failed:', error);
      return {
        success: false,
        message: error.message || 'Upgrade failed. Please try again.'
      };
    }
  }

  // Get valid t-shirt NFC keys (for display purposes)
  async getValidNFCKeys() {
    return await nfcConfigManager.getValidNFCKeys();
  }

  // Get detailed license information for debugging
  async getDetailedLicenseInfo() {
    const user = authManager.getCurrentUser();
    const storedLicense = localStorage.getItem('premium_license');
    let licenseData = null;

    if (storedLicense) {
      try {
        licenseData = JSON.parse(storedLicense);
      } catch (e) {
        licenseData = { error: 'Invalid JSON' };
      }
    }

    const validNFCKeys = await nfcConfigManager.getValidNFCKeys();

    return {
      user: user ? {
        email: user.email,
        authMethod: user.authMethod
      } : null,
      validNFCKeys: validNFCKeys,
      storedLicense: licenseData,
      isPremium: this.isPremiumUser,
      type: this.licenseType,
      features: this.isPremiumUser ? PREMIUM_FEATURES : [],
      hasChecked: this.hasCheckedLicense
    };
  }

  // Reset license (for testing)
  resetLicense() {
    localStorage.removeItem('premium_license');
    this.isPremiumUser = false;
    this.licenseType = null;
    this.hasCheckedLicense = false;
    console.log('🔄 License reset');
  }

  // Redeem promo code or magic link
  async redeemPromoCode(code) {
    try {
      const user = authManager.getCurrentUser();
      if (!user || !user.email) {
        throw new Error('You must be logged in to redeem a code');
      }

      console.log('🎫 Redeeming code:', code);

      const response = await fetch('./api/redeem-code.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: code.trim().toUpperCase(),
          email: user.email
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to redeem code');
      }

      // Store the license locally
      const license = {
        type: 'promo_code',
        code: code,
        userEmail: user.email,
        granted: Date.now(),
        expires: result.license.expiresAt ? new Date(result.license.expiresAt).getTime() : Date.now() + (365 * 24 * 60 * 60 * 1000),
        valid: true,
        features: PREMIUM_FEATURES,
        isPermanent: result.license.isPermanent
      };

      localStorage.setItem('premium_license', JSON.stringify(license));

      this.isPremiumUser = true;
      this.licenseType = 'promo_code';
      this.hasCheckedLicense = true;

      console.log('✅ Premium code redeemed successfully!');
      console.log('🎁 License type:', license.isPermanent ? 'Permanent' : 'Temporary');

      // Dispatch event for UI updates
      window.dispatchEvent(new CustomEvent('license-updated', {
        detail: {
          isPremium: true,
          type: 'promo_code',
          isPermanent: license.isPermanent,
          expiresAt: license.expires
        }
      }));

      return {
        success: true,
        message: result.message,
        license: license
      };

    } catch (error) {
      console.error('❌ Failed to redeem code:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default new LicenseManager();